import { useCallback, useRef, useState } from 'react';
import { OUNode } from '../types';

export type DragKind = 'ou' | 'site';

export interface DragPayload {
  kind: DragKind;
  id: string;
}

interface Callbacks {
  onSiteDropOnOU: (siteId: string, ouNodeId: string) => void;
  onOUReparent: (ouId: string, newParentId: string) => void;
  onOUReorder: (ouId: string, targetId: string, position: 'before' | 'after', targetParentId: string | null) => void;
  getNodes: () => OUNode[];
}

export function useDragDrop(callbacks: Callbacks) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [draggingKind, setDraggingKind] = useState<DragKind | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragInsert, setDragInsert] = useState<{ targetId: string; position: 'before' | 'after' } | null>(null);

  const dragPayload = useRef<DragPayload | null>(null);
  const dragInsertRef = useRef<{ targetId: string; position: 'before' | 'after' } | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, kind: DragKind, id: string) => {
    dragPayload.current = { kind, id };
    setDraggingId(id);
    setDraggingKind(kind);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }, []);

  const handleOUDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    const payload = dragPayload.current;
    if (!payload || payload.kind !== 'ou') return;
    if (payload.id === id) {
      e.dataTransfer.dropEffect = 'none';
      setDragOverId(null);
      dragInsertRef.current = null;
      setDragInsert(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const relY = (e.clientY - rect.top) / rect.height;
    if (relY < 0.35) {
      const insert = { targetId: id, position: 'before' as const };
      dragInsertRef.current = insert;
      setDragInsert(insert);
      setDragOverId(null);
      e.dataTransfer.dropEffect = 'move';
    } else if (relY > 0.65) {
      const insert = { targetId: id, position: 'after' as const };
      dragInsertRef.current = insert;
      setDragInsert(insert);
      setDragOverId(null);
      e.dataTransfer.dropEffect = 'move';
    } else {
      const targetNode = callbacks.getNodes().find(n => n.id === id);
      dragInsertRef.current = null;
      setDragInsert(null);
      if (targetNode?.type === 'Location') {
        e.dataTransfer.dropEffect = 'none';
        setDragOverId(null);
      } else {
        e.dataTransfer.dropEffect = 'move';
        setDragOverId(id);
      }
    }
  }, [callbacks]);

  // Drop from Sites pane onto OU row
  const handleSiteDropOnOU = useCallback((e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    const payload = dragPayload.current;
    dragPayload.current = null;
    dragInsertRef.current = null;
    setDragOverId(null);
    setDragInsert(null);
    setDraggingId(null);
    setDraggingKind(null);
    if (!payload || payload.kind !== 'site') return;
    callbacks.onSiteDropOnOU(payload.id, targetNodeId);
  }, [callbacks]);

  // Drop within OU tree (reparent or reorder)
  const handleOUDrop = useCallback((e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    const payload = dragPayload.current;
    const insert = dragInsertRef.current;
    dragPayload.current = null;
    dragInsertRef.current = null;
    setDragOverId(null);
    setDragInsert(null);
    setDraggingId(null);
    setDraggingKind(null);
    if (!payload || payload.kind !== 'ou') return;
    if (payload.id === targetNodeId) return;

    if (insert) {
      const targetNode = callbacks.getNodes().find(n => n.id === targetNodeId);
      if (!targetNode) return;
      callbacks.onOUReorder(payload.id, targetNodeId, insert.position, targetNode.parentId);
    } else {
      const targetNode = callbacks.getNodes().find(n => n.id === targetNodeId);
      if (targetNode?.type === 'Location') return;
      callbacks.onOUReparent(payload.id, targetNodeId);
    }
  }, [callbacks]);

  const handleDragLeave = useCallback((_e: React.DragEvent) => {
    setDragOverId(null);
    dragInsertRef.current = null;
    setDragInsert(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDraggingKind(null);
    setDragOverId(null);
    dragInsertRef.current = null;
    setDragInsert(null);
    dragPayload.current = null;
  }, []);

  const clearDragOver = useCallback(() => {
    setDragOverId(null);
  }, []);

  const highlightDropTarget = useCallback((id: string | null) => {
    setDragOverId(id);
    dragInsertRef.current = null;
    setDragInsert(null);
  }, []);

  const getDragPayload = useCallback(() => dragPayload.current, []);

  return {
    draggingId,
    draggingKind,
    dragOverId,
    dragInsert,
    handleDragStart,
    handleOUDragOver,
    handleSiteDropOnOU,
    handleOUDrop,
    handleDragLeave,
    handleDragEnd,
    clearDragOver,
    highlightDropTarget,
    getDragPayload,
  };
}
